import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Descriptions, Input, Spin } from "antd";
import {
  CheckCircleOutlined,
  DollarOutlined,
  SaveOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import apiClient from "../actions/api";
import "../style/job-board-settings.scss";

export default function JobBoardSettings({ title }) {
  document.title = title;
  const [form, setForm] = useState({
    boardApi: "",
    fallbackProfileId: "",
    bearerToken: "",
  });
  const [loading, setLoading] = useState(true),
    [saving, setSaving] = useState(false),
    [checking, setChecking] = useState(false),
    [balanceLoading, setBalanceLoading] = useState(false);
  const [decoded, setDecoded] = useState(null),
    [balance, setBalance] = useState(null),
    [message, setMessage] = useState(null);
  useEffect(() => {
    apiClient
      .get("/job-board/settings")
      .then(({ data }) => setForm(data))
      .catch((e) =>
        setMessage({
          type: "error",
          text: e.response?.data?.message || "Unable to load settings.",
        }),
      )
      .finally(() => setLoading(false));
  }, []);
  const update = (key) => (e) =>
    setForm((old) => ({ ...old, [key]: e.target.value }));
  const check = async () => {
    setChecking(true);
    setMessage(null);
    try {
      const { data } = await apiClient.post("/job-board/decode-token", {
        bearerToken: form.bearerToken,
      });
      setDecoded(data);
    } catch (e) {
      setDecoded(null);
      setMessage({
        type: "error",
        text: e.response?.data?.message || "Invalid token.",
      });
    } finally {
      setChecking(false);
    }
  };
  const checkBalance = async () => {
    setBalanceLoading(true);
    setMessage(null);
    try {
      const { data } = await apiClient.get("/job-board/balance");
      setBalance(data);
    } catch (e) {
      setBalance(null);
      setMessage({
        type: "error",
        text:
          e.response?.data?.message || "Unable to retrieve the BidHub balance.",
      });
    } finally {
      setBalanceLoading(false);
    }
  };
  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await apiClient.put("/job-board/settings", form);
      setForm(data);
      setMessage({ type: "success", text: "Job Board settings saved." });
    } catch (e) {
      setMessage({
        type: "error",
        text: e.response?.data?.message || "Unable to save settings.",
      });
    } finally {
      setSaving(false);
    }
  };
  if (loading)
    return (
      <div className="job-settings-loading">
        <Spin />
      </div>
    );
  return (
    <div className="job-settings-page">
      <div className="job-settings-heading">
        <span>
          <SettingOutlined />
        </span>
        <div>
          <h1>Job Board settings</h1>
          <p>Manage the BidHub connection used across the Job Board.</p>
        </div>
      </div>
      {message && <Alert showIcon type={message.type} message={message.text} />}
      <Card className="job-settings-card" title="Connection">
        <label>Board API</label>
        <Input
          value={form.boardApi}
          onChange={update("boardApi")}
          placeholder="https://api.bidhub.tech/app/board"
        />
        <label>Fallback profile ID</label>
        <Input
          value={form.fallbackProfileId}
          onChange={update("fallbackProfileId")}
        />
        <label>Bearer token</label>
        <div className="token-row">
          <Input.Password
            value={form.bearerToken}
            onChange={update("bearerToken")}
            visibilityToggle
          />
          <Button
            icon={<CheckCircleOutlined />}
            loading={checking}
            onClick={check}
          >
            Check token
          </Button>
          <Button
            icon={<DollarOutlined />}
            loading={balanceLoading}
            onClick={checkBalance}
          >
            Check balance
          </Button>
        </div>
        {balance !== null && (
          <div className="balance-result">
            <strong>BidHub balance</strong>
            <pre>
              {typeof balance === "object"
                ? JSON.stringify(balance, null, 2)
                : String(balance)}
            </pre>
          </div>
        )}
        {decoded && (
          <Descriptions
            className="token-details"
            bordered
            size="small"
            column={1}
          >
            <Descriptions.Item label="Created">
              {decoded.createdAt
                ? new Date(decoded.createdAt).toLocaleString()
                : "Not provided"}
            </Descriptions.Item>
            <Descriptions.Item label="Expires">
              {decoded.expiresAt
                ? new Date(decoded.expiresAt).toLocaleString()
                : "Not provided"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <span
                className={decoded.expired ? "token-expired" : "token-valid"}
              >
                {decoded.expired ? "Expired" : "Valid"}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Claims">
              <pre>{JSON.stringify(decoded.payload, null, 2)}</pre>
            </Descriptions.Item>
          </Descriptions>
        )}
        <div className="settings-actions">
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={save}
          >
            Save settings
          </Button>
        </div>
      </Card>
    </div>
  );
}
